import { useEffect, useMemo, useState } from "react";
import { fetchProductAlternatives } from "../../../utils/fetchProductAlternatives";
import resolveSimilarProducts from "../../../utils/resolveSimilarProducts";
import useHomeChatMatchSession from "../../../hooks/useHomeChatMatchSession";
import { resolveMatchIntent } from "../../../utils/resolveMatchIntent";
import { getAiSessionDependencyKey, readHomeChatMatchCache } from "../../../utils/homeChatMatchPersistence";

const useSimilarProducts = ({
  product,
  similar = [],
  user,
  limit = 6,
  aiSession: aiSessionProp = undefined,
}) => {
  const [alternativesResult, setAlternativesResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const sessionFromHook = useHomeChatMatchSession(
    aiSessionProp === undefined ? user : null,
    { syncServer: false },
  );
  const aiSession =
    aiSessionProp !== undefined ? aiSessionProp : sessionFromHook;
  const sessionKey = useMemo(
    () => getAiSessionDependencyKey(aiSession),
    [aiSession],
  );
  const userId = user?._id || user?.id || null;
  const [wizardAnswers, setWizardAnswers] = useState(
    () =>
      resolveMatchIntent({
        aiSession: aiSessionProp ?? sessionFromHook,
        user,
      }).wizardAnswers,
  );

  useEffect(() => {
    const next = resolveMatchIntent({ aiSession, user }).wizardAnswers;
    setWizardAnswers((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });

    const onStorage = (event) => {
      if (
        event.key === "homeChatMatchCache" ||
        event.key === "wizardAnswers" ||
        event.key === "matchProfile"
      ) {
        setWizardAnswers(
          resolveMatchIntent({
            aiSession: readHomeChatMatchCache(userId),
            user,
          }).wizardAnswers,
        );
      }
    };

    const onChatUpdated = () => {
      const updated = resolveMatchIntent({
        aiSession: readHomeChatMatchCache(userId),
        user,
      }).wizardAnswers;
      setWizardAnswers((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
        return updated;
      });
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("homeChatMatchUpdated", onChatUpdated);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("homeChatMatchUpdated", onChatUpdated);
    };
  }, [sessionKey, user, userId]);

  useEffect(() => {
    if (!product?._id) {
      setAlternativesResult(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      const result = await fetchProductAlternatives(product._id, {
        user,
        limit,
        aiSession,
      });
      if (cancelled) return;
      setAlternativesResult(result);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [product?._id, user?.id, user?._id, limit, sessionKey]);

  const resolved = useMemo(
    () =>
      resolveSimilarProducts({
        currentProduct: product,
        categorySimilar: similar,
        alternativesApiResult: alternativesResult,
        aiSession,
        wizardAnswers,
        limit,
      }),
    [product, similar, alternativesResult, aiSession, wizardAnswers, limit],
  );

  return {
    items: resolved.items,
    source: resolved.source,
    contextSummary: resolved.contextSummary,
    aiSession,
    loading,
  };
};

export default useSimilarProducts;
